import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Plus, RefreshCw, Slash, Check, Trash2, Trophy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return ''; }
};

// Convert ISO → "YYYY-MM-DDTHH:mm" suitable for <input type="datetime-local"> (in local time)
const toLocalInput = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
};

const emptyGame = (n) => ({
    game_number: n,
    home_team: '',
    away_team: '',
    commence_time: '',
});

/**
 * Admin panel for Series Pack — playoff series prediction.
 *
 * Flow:
 *   1. Enter series name + the two teams + 7 games (auto-fills home/away by alternating)
 *   2. Set entry price + payout tiers ($5 / $25 / $50 / $500 jackpot defaults)
 *   3. Launch — auto-cancels any active series (refunds pending entries) + notifies players
 *   4. After each game, grade winner. When series ends, click "Settle" to pay everyone.
 */
export const SeriesPackAdminPanel = () => {
    const [name, setName] = useState('NBA Finals — Pick the Series');
    const [teamA, setTeamA] = useState('');
    const [teamB, setTeamB] = useState('');
    const [entryPrice, setEntryPrice] = useState(5);
    const [maxGames, setMaxGames] = useState(7);
    const [minGames, setMinGames] = useState(4);
    const [payout4, setPayout4] = useState(5);
    const [payout5, setPayout5] = useState(25);
    const [payout6, setPayout6] = useState(50);
    const [jackpot, setJackpot] = useState(500);
    const [games, setGames] = useState(() => Array.from({ length: 7 }, (_, i) => emptyGame(i + 1)));
    const [submitting, setSubmitting] = useState(false);

    const [packs, setPacks] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [resultDrafts, setResultDrafts] = useState({});  // {[packId-game]: 'team_a'|'team_b'|'void'}

    const loadHistory = useCallback(async () => {
        setListLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/series-packs`);
            setPacks(data.rows || []);
        } catch { toast.error('Could not load series packs'); }
        setListLoading(false);
    }, []);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    // Whenever maxGames or teams change, resize the games array and auto-fill home/away alternating (1,2 at team_a home, 3,4 at team_b home, 5,6 at team_a home, 7 at team_b home → standard NBA 2-2-1-1-1)
    useEffect(() => {
        const n = Math.max(3, Math.min(10, Number(maxGames) || 7));
        setGames(prev => {
            const next = [];
            for (let i = 0; i < n; i++) {
                const existing = prev[i];
                let home = '', away = '';
                if (teamA && teamB) {
                    // 2-2-1-1-1 format (NBA): games 1,2,5,7 → team_a home; 3,4,6 → team_b home
                    const aHomeGames = new Set([1, 2, 5, 7]);
                    if (aHomeGames.has(i + 1)) { home = teamA; away = teamB; }
                    else { home = teamB; away = teamA; }
                }
                next.push({
                    game_number: i + 1,
                    home_team: existing?.home_team || home,
                    away_team: existing?.away_team || away,
                    commence_time: existing?.commence_time || '',
                });
            }
            return next;
        });
    }, [maxGames, teamA, teamB]);

    const setGameField = (i, field, val) => {
        setGames(prev => {
            const next = [...prev];
            next[i] = { ...next[i], [field]: val };
            return next;
        });
    };

    const launch = async () => {
        if (!name.trim() || !teamA.trim() || !teamB.trim()) {
            toast.error('Set series name and both team names');
            return;
        }
        if (teamA.trim() === teamB.trim()) {
            toast.error('Teams must be different');
            return;
        }
        const validGames = games.map((g, i) => ({
            game_number: i + 1,
            home_team: g.home_team.trim(),
            away_team: g.away_team.trim(),
            commence_time: g.commence_time ? new Date(g.commence_time).toISOString() : null,
        }));
        for (const g of validGames) {
            if (!g.home_team || !g.away_team) {
                toast.error(`Game ${g.game_number}: set both teams`);
                return;
            }
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/admin/series-pack`, {
                name: name.trim(),
                team_a: teamA.trim(),
                team_b: teamB.trim(),
                entry_price: Number(entryPrice),
                max_games: Number(maxGames),
                min_games: Number(minGames),
                payouts: {
                    '4': Number(payout4),
                    '5': Number(payout5),
                    '6': Number(payout6),
                    'jackpot': Number(jackpot),
                },
                games: validGames,
                notify: true,
            });
            toast.success('Series Pack launched!');
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not create series');
        }
        setSubmitting(false);
    };

    const gradeGame = async (packId, gameNumber, winner) => {
        try {
            await axios.post(`${API}/admin/series-pack/${packId}/grade-game`, {
                game_number: gameNumber,
                winner,
            });
            toast.success(`Game ${gameNumber} → ${winner}`);
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not grade game');
        }
    };

    const settle = async (packId) => {
        if (!window.confirm('Settle the series now? Pays all winners — irreversible.')) return;
        try {
            const { data } = await axios.post(`${API}/admin/series-pack/${packId}/settle`);
            toast.success(`Settled — ${data.winners_count} winners, $${Number(data.total_payouts).toFixed(2)} paid (${data.actual_played} games played).`);
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not settle');
        }
    };

    const cancel = async (packId) => {
        if (!window.confirm('Cancel the series and refund all entries?')) return;
        try {
            const { data } = await axios.post(`${API}/admin/series-pack/${packId}/cancel`);
            toast.success(`Cancelled — refunded ${data.refunded} entries.`);
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not cancel');
        }
    };

    const activePack = packs.find(p => p.status === 'active');

    return (
        <div className="space-y-5" data-testid="series-pack-admin">
            {/* ── BUILDER ── */}
            <div className="card-dark p-5" style={{ borderLeft: '3px solid #FF8C00' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-5 h-5 text-[#FF8C00]" />
                    <h3 className="text-lg font-bold text-white">Series Pack — Playoff Series Prediction</h3>
                </div>
                <p className="text-xs text-[#A0AAB2] mb-4">
                    Players predict <strong>how many games the series ends in</strong> + the winner of each.
                    Hit 4/5/6 picks for tier payout. Nail exact length + every winner = JACKPOT.
                    Launching a new one auto-replaces the active series (refunds pending entries).
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Series name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                            data-testid="sp-name"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Team A</Label>
                            <Input
                                value={teamA}
                                onChange={(e) => setTeamA(e.target.value)}
                                placeholder="Lakers"
                                className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                data-testid="sp-team-a"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Team B</Label>
                            <Input
                                value={teamB}
                                onChange={(e) => setTeamB(e.target.value)}
                                placeholder="Celtics"
                                className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                data-testid="sp-team-b"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Entry $</Label>
                        <Input type="number" value={entryPrice} onChange={(e) => setEntryPrice(e.target.value)} className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm" data-testid="sp-entry" />
                    </div>
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Best of</Label>
                        <Input type="number" value={maxGames} onChange={(e) => setMaxGames(e.target.value)} className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm" data-testid="sp-max-games" />
                    </div>
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Min length</Label>
                        <Input type="number" value={minGames} onChange={(e) => setMinGames(e.target.value)} className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm" data-testid="sp-min-games" />
                    </div>
                    <div className="text-[10px] text-[#A0AAB2] flex items-end pb-2">
                        Players can predict <br /><strong className="text-white">{minGames}–{maxGames} games</strong>
                    </div>
                </div>

                {/* Payout tiers */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">4 hits $</Label>
                        <Input type="number" value={payout4} onChange={(e) => setPayout4(e.target.value)} className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm" data-testid="sp-payout-4" />
                    </div>
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">5 hits $</Label>
                        <Input type="number" value={payout5} onChange={(e) => setPayout5(e.target.value)} className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm" data-testid="sp-payout-5" />
                    </div>
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">6 hits $</Label>
                        <Input type="number" value={payout6} onChange={(e) => setPayout6(e.target.value)} className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm" data-testid="sp-payout-6" />
                    </div>
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">JACKPOT $</Label>
                        <Input type="number" value={jackpot} onChange={(e) => setJackpot(e.target.value)} className="h-9 bg-[#0a0a0a] border-white/10 text-white text-sm" data-testid="sp-jackpot" />
                    </div>
                </div>

                {/* Games table */}
                <div className="rounded-lg border border-white/10 bg-[#0a0a0a] overflow-hidden mb-4">
                    <table className="w-full text-xs">
                        <thead className="bg-[#0e0e0e] text-[#A0AAB2] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-2 py-2">#</th>
                                <th className="text-left px-2 py-2">Away</th>
                                <th className="text-left px-2 py-2">Home</th>
                                <th className="text-left px-2 py-2">Tipoff (CT)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {games.map((g, i) => (
                                <tr key={i} className="border-t border-white/5">
                                    <td className="px-2 py-1 text-white font-bold">{i + 1}</td>
                                    <td className="px-2 py-1">
                                        <Input
                                            value={g.away_team}
                                            onChange={(e) => setGameField(i, 'away_team', e.target.value)}
                                            className="h-7 bg-[#0e0e0e] border-white/10 text-white text-xs"
                                            data-testid={`sp-game-away-${i}`}
                                        />
                                    </td>
                                    <td className="px-2 py-1">
                                        <Input
                                            value={g.home_team}
                                            onChange={(e) => setGameField(i, 'home_team', e.target.value)}
                                            className="h-7 bg-[#0e0e0e] border-white/10 text-white text-xs"
                                            data-testid={`sp-game-home-${i}`}
                                        />
                                    </td>
                                    <td className="px-2 py-1">
                                        <Input
                                            type="datetime-local"
                                            value={g.commence_time}
                                            onChange={(e) => setGameField(i, 'commence_time', e.target.value)}
                                            className="h-7 bg-[#0e0e0e] border-white/10 text-white text-xs"
                                            data-testid={`sp-game-time-${i}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Button
                    onClick={launch}
                    disabled={submitting}
                    className="w-full font-black h-11"
                    style={{ background: 'linear-gradient(135deg, #FF8C00, #7C3AED)', color: 'white' }}
                    data-testid="sp-launch-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {submitting ? 'Launching…' : 'Launch Series Pack'}
                </Button>
            </div>

            {/* ── ACTIVE / HISTORY ── */}
            <div className="card-dark p-5">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-[#FFD700]" />
                        Series Packs
                    </h3>
                    <Button variant="outline" size="sm" onClick={loadHistory} disabled={listLoading} className="border-white/10 text-white" data-testid="sp-refresh-btn">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${listLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {activePack && (
                    <div className="rounded-lg border border-[#FF8C00]/40 bg-[#FF8C00]/5 p-3 mb-4" data-testid="sp-active-pack">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-[#FF8C00] font-black">ACTIVE</p>
                                <p className="text-white font-black text-base">{activePack.name}</p>
                                <p className="text-[#A0AAB2] text-xs">{activePack.team_a} vs {activePack.team_b} · ${activePack.entry_price} entry · {activePack.total_entries} entries</p>
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={() => settle(activePack.id)} size="sm" className="bg-[#39FF14]/20 hover:bg-[#39FF14]/30 text-[#39FF14] border border-[#39FF14]/40" data-testid="sp-settle-btn">
                                    <Check className="w-3.5 h-3.5 mr-1" /> Settle
                                </Button>
                                <Button onClick={() => cancel(activePack.id)} size="sm" variant="outline" className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid="sp-cancel-btn">
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
                                </Button>
                            </div>
                        </div>

                        {/* Grade games */}
                        <div className="space-y-1.5">
                            {(activePack.games || []).map((g, i) => {
                                const winner = g.winner;
                                const teams = [activePack.team_a, activePack.team_b, 'void'];
                                return (
                                    <div key={i} className="flex items-center gap-2 text-xs rounded-md bg-[#0a0a0a] border border-white/10 px-2 py-1.5">
                                        <span className="font-bold text-white w-12">G{i + 1}</span>
                                        <span className="text-[#A0AAB2] flex-1 truncate">
                                            {g.away_team} @ {g.home_team} {g.commence_time && <span className="text-[10px]">· {fmtCT(g.commence_time)}</span>}
                                        </span>
                                        <div className="flex gap-1">
                                            {teams.map(t => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => gradeGame(activePack.id, i + 1, t)}
                                                    className={`px-2 py-1 rounded text-[10px] font-bold border transition ${winner === t
                                                        ? (t === 'void' ? 'bg-[#FFD700]/20 border-[#FFD700] text-[#FFD700]' : 'bg-[#39FF14]/20 border-[#39FF14] text-[#39FF14]')
                                                        : 'bg-transparent border-white/10 text-[#A0AAB2] hover:border-white/30'
                                                        }`}
                                                    data-testid={`sp-grade-${i}-${t.replace(/\s+/g, '-')}`}
                                                >
                                                    {t === 'void' ? <Slash className="w-3 h-3 inline" /> : t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* History */}
                <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-[#0e0e0e] text-[#A0AAB2] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-2 py-2">Created</th>
                                <th className="text-left px-2 py-2">Name</th>
                                <th className="text-left px-2 py-2">Status</th>
                                <th className="text-right px-2 py-2">Entries</th>
                                <th className="text-right px-2 py-2">Paid</th>
                            </tr>
                        </thead>
                        <tbody>
                            {packs.length === 0 ? (
                                <tr><td colSpan={5} className="text-center text-[11px] text-[#A0AAB2] py-4">No series yet.</td></tr>
                            ) : packs.map(p => (
                                <tr key={p.id} className="border-t border-white/5">
                                    <td className="px-2 py-1.5 text-[#A0AAB2]">{fmtCT(p.created_at)}</td>
                                    <td className="px-2 py-1.5 text-white">{p.name}</td>
                                    <td className="px-2 py-1.5">
                                        <span className={`text-[10px] font-bold uppercase ${p.status === 'active' ? 'text-[#39FF14]' : p.status === 'settled' ? 'text-[#FFD700]' : 'text-[#A0AAB2]'}`}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="px-2 py-1.5 text-right text-white font-mono">{p.total_entries}</td>
                                    <td className="px-2 py-1.5 text-right text-[#39FF14] font-mono">${Number(p.total_payouts).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SeriesPackAdminPanel;
